// @ts-check

import { service } from '@/core/constants'
import { accountTransactionsRoutes } from '@/router/accountTransactionsRoutes'
import { companyFoundingRoutes } from '@/router/companyFoundingRoutes'
import { customerRoutes } from '@/router/customerRoutes'
import { drivingLogRoutes } from '@/router/drivingLogRoutes'
import { expenseRoutes } from '@/router/expenseRoutes'
import { incomeRoutes } from '@/router/incomeRoutes'
import { invoiceRoutes } from '@/router/invoiceRoutes'
import { loginRoutes } from '@/router/loginRoutes'
import { ROUTE_LAYOUT, ROUTE_NAME } from '@/router/meta'
import { onboardingRoutes } from '@/router/onboardingRoutes'
import { productRoutes } from '@/router/productRoutes'
import { registrationRoutes } from '@/router/registrationRoutes'
import { setupReportRoutes } from '@/router/reportsRoutes'
import { salaryRoutes } from '@/router/salaryRoutes'
import { setupSettingsRoutes } from '@/router/settingsRoutes'
import ukkoturvaRoutes from '@/router/ukkoturvaRoutes'
import { store } from '@/store'
import BaseViewVisitor from '@/views/BaseViewVisitor.vue'
import analyticsRoutes from './analyticsRoutes'

const BANK_TRANSACTIONS = {
  path: '/bank-transactions',
  name: 'bank_transactions',
  component: () => import('@/views/BankTransactions.vue'),
  meta: {
    requiresAuth: true,
    showInMainNav: true,
    icon: '$bank-outline',
    [ROUTE_NAME]: 'bank_transactions'
  }
}

/** @returns {import('vue-router').RouteRecordRaw[]} */
export const setupRoutes = () => [
  ...loginRoutes,
  ...registrationRoutes,
  ...onboardingRoutes,
  ...(process.env.VUE_APP_SERVICE === service.OY ? companyFoundingRoutes : []),
  {
    path: '/dashboard',
    name: 'dashboard',
    alias: '/dashboard/setup-complete',
    component: () => import('@/views/DashboardView.vue'),
    meta: {
      requiresAuth: true,
      showInMainNav: true,
      icon: '$apps',
      [ROUTE_NAME]: 'dashboard'
    }
  },
  {
    path: '/kyc-questionnaire',
    name: 'kyc-questionnaire',
    component: () => import('@/views/KnowYourCustomer.vue'),
    beforeEnter: (to, from, next) => {
      // We do not want users to have access to the KYC page if not required to answer
      if (
        store.getters['companyData/isInDemoMode'] ||
        !store.getters['companyData/isKycRequired']
      ) {
        next({ name: 'dashboard', replace: true })
      } else {
        next()
      }
    },
    meta: {
      showInMainNaw: false,
      requiresAuth: true,
      [ROUTE_NAME]: 'kyc-questionnaire'
    }
  },
  {
    path: '/tax-returns/:id/questionnaire',
    name: 'edit_tax_return_questionnaire',
    component: () =>
      import('@/views/tax-returns/TaxReturnQuestionnaireCreate.vue'),
    beforeEnter: (to, from, next) => {
      if (!store.getters['companyData/isTaxReturnQuestionnairePeriod']) {
        next({ name: 'dashboard', replace: true })
      } else {
        next()
      }
    },
    props: (route) => ({ taxReturnId: +route.params.id }),
    meta: {
      requiresAuth: true,
      showInMainNav: false,
      icon: '$package-variant-closed',
      [ROUTE_NAME]: 'edit_tax_return_questionnaire'
    }
  },
  ...(process.env.VUE_APP_SERVICE === service.TLP
    ? [...accountTransactionsRoutes]
    : [...[BANK_TRANSACTIONS]]),
  ...(process.env.VUE_APP_SERVICE === service.OY ? [...salaryRoutes] : []),
  ...incomeRoutes,
  ...expenseRoutes,
  ...invoiceRoutes,
  ...setupReportRoutes(),
  ...analyticsRoutes,
  ...customerRoutes,
  ...productRoutes,
  // Disable driving log routes for OY service
  ...(process.env.VUE_APP_SERVICE !== service.OY ? [...drivingLogRoutes] : []),
  ...setupSettingsRoutes(),
  ...ukkoturvaRoutes,
  {
    path: '/reset-password/:pathMatch(.*)*',
    name: 'forgot-password',
    component: () => import('@/views/PasswordReset.vue'),
    meta: {
      requiresAuth: false,
      showInMainNav: false,
      [ROUTE_LAYOUT]: BaseViewVisitor,
      [ROUTE_NAME]: 'forgot-password'
    }
  },
  {
    path: '/reset-password2/:pathMatch(.*)*',
    redirect: { name: 'forgot-password' }
  },
  {
    path: '/set-password/:pathMatch(.*)*',
    name: 'set-password',
    component: () => import('@/views/PasswordReset.vue'),
    meta: {
      requiresAuth: false,
      showInMainNav: false,
      [ROUTE_LAYOUT]: BaseViewVisitor,
      [ROUTE_NAME]: 'set-password'
    }
  },
  {
    path: '/set-password2/:pathMatch(.*)*',
    redirect: { name: 'set-password' }
  },
  {
    path: '/terms-of-service',
    name: 'terms-of-service',
    component: () => import('@/views/TermsOfService.vue'),
    meta: {
      requiresAuth: false,
      showInMainNav: false,
      [ROUTE_LAYOUT]: BaseViewVisitor,
      [ROUTE_NAME]: 'terms-of-service'
    }
  },
  {
    path: '/terms-of-service/:id/accept',
    name: 'accept-terms-of-service',
    component: () => import('@/views/AcceptTerms.vue'),
    meta: {
      requiresAuth: true,
      showInMainNav: false,
      [ROUTE_NAME]: 'accept-terms-of-service'
    }
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/LoginAsUser.vue'),
    meta: {
      icon: '$open-in-new',
      requiresAuth: true,
      showInMainNav: false,
      [ROUTE_NAME]: 'admin'
    }
  },
  {
    path: '/admin/login',
    name: 'admin-login',
    component: () => import('@/views/admin/ImpersonateAsUserView.vue'),
    meta: {
      [ROUTE_NAME]: 'admin-login'
    },
    beforeEnter: (to, from, next) => {
      if (to.query.code) {
        store.commit('auth/clearAllData')
        next()
      } else {
        next({ name: 'login' })
      }
    }
  },
  {
    path: '/redirect',
    name: 'redirect',
    component: () => import('@/views/RedirectView.vue'),
    meta: {
      requiresAuth: true,
      showInMainNav: false,
      [ROUTE_NAME]: 'redirect'
    },
    children: [
      {
        path: 'swan-payment',
        component: () =>
          import(
            '@/components/business-account/BusinessAccountPaymentInfo.vue'
          ),
        name: 'swan_payment_redirect'
      },
      {
        path: 'swan-oauth',
        component: () =>
          import(
            '@/components/business-account/BusinessAccountFinalizeOnboarding.vue'
          ),
        name: 'swan_oauth_redirect'
      },
      {
        path: 'swan-close-account',
        component: () =>
          import('@/components/business-account/BusinessAccountClose.vue'),
        name: 'swan_close_account_redirect'
      },
      {
        path: '/:pathMatch(.*)*',
        component: () => import('@/views/NotFound.vue')
      }
    ]
  },
  {
    // Keep this as last route
    path: '/:pathMatch(.*)*',
    name: 'not_found',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      showInMainNav: false,
      requiresAuth: false,
      [ROUTE_LAYOUT]: BaseViewVisitor,
      [ROUTE_NAME]: 'not_found'
    }
  }
]

export default setupRoutes
